import { io } from "socket.io-client";
import { setTokenStatus, resetTokenStatus, clearUser } from "../../store/slices/userSlice";

let globalSocket = null;
let currentToken = null;
let isConnecting = false;

const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "") || "";
// HTTPS pages must use wss://; http pages use ws://
const SOCKET_BASE_URL =
  BASE_URL.replace(/^https/i, "wss").replace(/^http/i, "ws") || window.location.origin;

export const initGlobalSocket = (dispatch) => {
  const token = localStorage.getItem("linko_web");
  if (!token) {
    disconnectGlobalSocket(dispatch);
    return null;
  }

  // Already connected with this token
  if (globalSocket && currentToken === token && (globalSocket.connected || isConnecting)) {
    return globalSocket;
  }

  // If token changed or existing stale socket
  if (globalSocket) {
    try {
      globalSocket.removeAllListeners();
      globalSocket.disconnect();
    } catch (_e) {}
    globalSocket = null;
  }

  currentToken = token;
  isConnecting = true;

  const rawToken = token.startsWith("Bearer ") ? token.replace("Bearer ", "") : token;
  const bearerToken = token.startsWith("Bearer ") ? token : `Bearer ${token}`;

  // Connect to the /audio-translation namespace where the backend emits token-status
  const namespaceUrl = `${SOCKET_BASE_URL}/audio-translation`;

  const sock = io(namespaceUrl, {
    transports: ["websocket"],
    upgrade: false,
    auth: {
      token: bearerToken,
      authorization: bearerToken,
    },
    query: {
      token: rawToken,
    },
    extraHeaders: {
      Authorization: bearerToken,
    },
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1500,
  });

  const handleTokenStatus = (payload) => {
    // console.log("[Global Socket] 'token-status' event received:", payload);
    if (dispatch) {
      dispatch(setTokenStatus(payload));
    }
    try {
      window.dispatchEvent(
        new CustomEvent("tokenStatusUpdated", { detail: payload })
      );
    } catch (_e) {}
  };

  sock.on("connect", () => {
    isConnecting = false;
  });

  // Listen to token-status event
  sock.on("token-status", handleTokenStatus);
  sock.on("token_status", handleTokenStatus);
  sock.on("tokenStatus", handleTokenStatus);

  // Catch-all handler to process backend socket messages
  sock.onAny((event, ...args) => {
    if (event.toLowerCase().includes("token")) {
      handleTokenStatus(args[0]);
    }

    // Capture personalIdentifiersProtected from any payload so it reaches the UI
    // even if the per-translation listener was already detached.
    try {
      const argPayload = args[0];
      const candidates = [
        argPayload?.data,
        argPayload?.paragraph,
        argPayload?.paragraphs,
        ...(Array.isArray(argPayload?.paragraphs) ? argPayload.paragraphs : []),
        argPayload,
      ];
      for (const c of candidates) {
        const piVal =
          c?.personalIdentifiersProtected ??
          c?.personal_identifiers_protected ??
          c?.data?.personalIdentifiersProtected;
        if (piVal !== null && piVal !== undefined && piVal !== "" && !isNaN(Number(piVal))) {
          const piNum = Number(piVal);
          try {
            const prev = JSON.parse(localStorage.getItem("linko_personal_identifiers_protected") || "null");
            const next = {
              value: piNum,
              status: c?.status || argPayload?.status || "completed",
              targetLanguage: c?.targetLanguage || argPayload?.targetLanguage || null,
              originalText: c?.originalText || argPayload?.originalText || null,
              event,
              timestamp: Date.now(),
            };
            if (!prev || prev.value !== next.value || prev.status !== next.status) {
              localStorage.setItem("linko_personal_identifiers_protected", JSON.stringify(next));
              window.dispatchEvent(new CustomEvent("personalIdentifiersUpdated", { detail: next }));
            }
          } catch (_e) { }
          break;
        }
      }
    } catch (_e) { }

    const isErrorEvent =
      event.toLowerCase() === "error" ||
      event.toLowerCase().includes("error") ||
      event.toLowerCase().includes("fail");

    if (
      isErrorEvent ||
      event.toLowerCase().includes("uploadaudio") ||
      event.toLowerCase().includes("upload-audio") ||
      event.toLowerCase().includes("translation-result") ||
      event.toLowerCase().includes("translate-audio")
    ) {
      const raw = args[0];
      const d = raw?.data || raw;
      const isComplete =
        raw?.status === "completed" ||
        d?.status === "completed" ||
        raw?.responseType === "done" ||
        d?.responseType === "done";

      const isError =
        isErrorEvent ||
        raw?.status === "error" ||
        d?.status === "error" ||
        raw?.status === "failed" ||
        d?.status === "failed" ||
        raw?.status === false ||
        d?.status === false ||
        raw?.responseType === "error" ||
        d?.responseType === "error";

      if (isError) {
        const errMsg = d?.message || raw?.message || (typeof raw === "string" ? raw : "Please purchase a plan to access this feature.");
        try {
          localStorage.removeItem("linko_pending_transcribe");
          localStorage.removeItem("linko_transcribe_progress");
          localStorage.removeItem("linko_pending_translation");
          localStorage.removeItem("linko_active_translation");
        } catch (_) {}
        try {
          window.dispatchEvent(
            new CustomEvent("transcriptionError", {
              detail: {
                error: errMsg,
                partialResult: d || raw,
              },
            })
          );
          window.dispatchEvent(
            new CustomEvent("translationError", {
              detail: {
                error: errMsg,
                partialResult: d || raw,
              },
            })
          );
        } catch (_) {}
        return;
      }

      if (isComplete) {
        try {
          localStorage.removeItem("linko_pending_transcribe");
          localStorage.removeItem("linko_transcribe_progress");
        } catch (_) {}
        try {
          window.dispatchEvent(
            new CustomEvent("transcribeProgressUpdated", { detail: 100 })
          );
          window.dispatchEvent(
            new CustomEvent("transcriptionCompleted", { detail: raw })
          );
        } catch (_) {}
        return;
      }

      const percentageVal = d?.percentage ?? raw?.percentage ?? d?.progress ?? raw?.progress ?? null;
      if (percentageVal !== null && percentageVal !== undefined) {
        const p = Math.min(99, Math.max(0, Number(percentageVal)));
        try {
          localStorage.setItem("linko_transcribe_progress", String(p));
        } catch (_) {}
        try {
          window.dispatchEvent(
            new CustomEvent("transcribeProgressUpdated", { detail: p })
          );
        } catch (_) {}
      }
    }
  });

  sock.on("disconnect", () => {
    isConnecting = false;
  });

  sock.on("connect_error", () => {
    isConnecting = false;
  });

  globalSocket = sock;
  return sock;
};

export const disconnectGlobalSocket = (dispatch) => {
  isConnecting = false;
  if (globalSocket) {
    try {
      globalSocket.removeAllListeners();
      globalSocket.disconnect();
    } catch (_e) {}
    globalSocket = null;
    currentToken = null;

  }
  if (dispatch) {
    dispatch(resetTokenStatus());
    dispatch(clearUser());
  }
};

export const getGlobalSocket = () => globalSocket;

const SocketService = {
  initGlobalSocket,
  disconnectGlobalSocket,
  getGlobalSocket,
};

export default SocketService;
